import { inject as service } from '@ember/service';
import { all } from 'rsvp';
import Category from '@square/dashboard-items-data/models/category';
import ItemsV3Adapter from '@square/dashboard-items-data/adapters/items-v3-base';
import Item from '@square/dashboard-items-data/models/item';
import { ItemType, ItemTypesForFiltering_V3 } from '@square/dashboard-items-data/utils/item-type-constants';
import ItemVariation from '@square/dashboard-items-data/models/item-variation';
import { CATALOG_SERVICE_TYPES, CATALOG_SERVICE_QUERY_TYPES } from '@square/dashboard-data/models/catalog/catalog-service-constants';
import { A } from '@ember/array';
export default ItemsV3Adapter.extend({
  catalog: service(),
  /*
  This function handles making queries to the custom Elasticsearch-backed `search` endpoint available for more complex
  item queries. (proto: https://prototype.sqprod.co/#/docs/squareup.items.merchant.SearchCatalogRequest)
   These parameters are available when `useSearchCatalog: true` is passed to `query`. Otherwise, this
  function defaults to passing the query to the default `items-v3-base` query function.
   Query parameters:
    [text] - string query that will be matched using word-prefix matching against the following fields (at time of writing):
      Item: name, description, abbreviation
      Variation: name, sku, reverse sku (to allow matching tail of SKU)
      Vendor Info: vendor code (if `searchVendorCode` is true)
    [unitTokens] - array of merchant subunit tokens; will limit results to objects enabled at one or more of these locations
    [categories] - array of object tokens for categories; will limit results to items in one of the passed categories
    [productTypes=["REGULAR"]] - array of ItemType enum values; will exclude items not of one of the passed types
    [vendorTokens] - will limit results to items/variations that have an existing vendor info for at least one passed token
    [searchVendorCode=false] - if text query will match vendor info vendor codes
    [doesNotExist] - array of NullableField enums (https://prototype.sqprod.co/#/docs/squareup.items.merchant.SearchCatalogRequest.NullableField)
      If this is passed then it will limit results to those that DO NOT have a value for the corresponding field.
   Related object parameters:
    [includedRelationships] - array of relationships to automatically fetch for the record
      There are two allowed formats for entries in this array:
        1. a string relationship key, i.e. 'itemVariations'
        2. a JS Object with `key` and `includedRelationships` attributes, i.e. { key: 'itemVariations', includedRelationships: ['itemVariationVendorInfos'] }
       The object format can be used to recursively fetch nested relationships more than one step away from the original model
       NOTE: Because item variations and categories are returned as part of the initial `search` response, they will always be
      fetched and loaded regardless of their inclusion in `includedRelationships`.
   Paging parameters:
    [pageSize=125] - number of results to fetch
    [paginationToken] - token returned from last query with same parameters, used to fetch additional results
   Returns an EmberData `AdapterPopulatedRecordArray` (as all adapter queries do).
  The `meta` of the returned record array can have 2 properties:
    [nextPageToken] - string token that can be passed back to this function to fetch further results
    [matchedVariations] - a map of item tokens to lists of variation tokens. If an item token is a key in this map
      then the query only matched a subset of the variations of the item, not the item itself. If a returned item's id is
      NOT in a key in the map, then the item itself or all its variations matched the query.
      Relevant proto: https://prototype.sqprod.co/#/docs/squareup.items.merchant.DashboardSearchCatalogResponse
   Example Request:
    `this.store.query('item', {
      useSearchCatalog: true,
      text: 'Apple',
      vendorTokens: ['VENDOR_1'],
      nullableFields: ['VENDOR_TOKEN'],
      searchVendorCode: true,
      includedRelationships: [
        { key: 'itemVariations', includedRelationships: ['itemVariationVendorInfos'] }
      ],
    });`
   Example Response:
    records: [item1, item2, ...123 more],
    meta: {
      nextPageToken: 'crazyTokenASDFAWEFD',
      matchedVariations: {
        item1: [variationA, variationB]
      }
    }
  */
  async query(store, type, query) {
    const defaultProductTypes = [...ItemTypesForFiltering_V3, ItemType.APPOINTMENTS_SERVICE];
    const productTypes = query.productTypes ?? defaultProductTypes;
    if (!query.useSearchCatalog) {
      if (!query.attributeQueries) {
        query.attributeQueries = [];
      }
      // convert product type query to standard v3 attribute query
      query.attributeQueries.push({
        type: CATALOG_SERVICE_QUERY_TYPES.SET,
        attribute: 'type',
        value: productTypes.map(p => ItemType.serialize(p).toString())
      });
      delete query.productTypes;
      return this._super(store, type, query);
    }
    const searchParams = {
      ...query,
      productTypes
    };
    const {
      objects,
      paginationToken,
      matchedVariations
    } = await this.catalog.searchCatalog(searchParams);
    const objectsByType = this._groupByCatalogType(objects);
    const items = objectsByType[CATALOG_SERVICE_TYPES[Item.catalogType()]] || [];
    const variations = objectsByType[CATALOG_SERVICE_TYPES[ItemVariation.catalogType()]] || [];
    const categories = objectsByType[CATALOG_SERVICE_TYPES[Category.catalogType()]] || [];
    let includedRelationships = query.includedRelationships || [];
    const relatedObjectPromises = [];
    if (items.length > 0) {
      const variationEntry = includedRelationships.find(relationship => relationship === 'itemVariations' || relationship.key === 'itemVariations');

      // fetch all relationships except variations and categories, which are already fetched
      if (variationEntry) {
        includedRelationships = includedRelationships.without(variationEntry).without('menuCategory');
      }
      relatedObjectPromises.push(this._fetchRelatedObjects(type, items, includedRelationships));

      // fetch nested relationships for variations if requested
      if (typeof variationEntry === 'object') {
        relatedObjectPromises.push(this._fetchRelatedObjects(store.modelFor('item-variation'), variations, variationEntry.includedRelationships));
      }
    }
    const additionalRelatedObjectResponses = await all(relatedObjectPromises);
    const additionalRelatedObjects = additionalRelatedObjectResponses.reduce((allObjects, responseObjects) => {
      return A(allObjects).pushObjects(responseObjects);
    }, []);
    if (items.length > 0) {
      // The relatedObjects attached to returned objects will be sideloaded by the store.
      // It isn't important for them to be specifically linked to their related objects
      // so we add them all to the first item here for simplicity.
      A(items).firstObject.relatedObjects = [...variations, ...categories, ...additionalRelatedObjects];
    }
    items.meta = {
      nextPageToken: paginationToken,
      matchedVariations
    };
    return items;
  }
});